<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="inventory-actions">
				<v-btn depressed outlined @click="$router.push('/inventory/bin-utility')">Cancel</v-btn>
				<v-btn v-if="canUpdateBin" depressed color="primary" @click="saveItemStock()">Save</v-btn>
			</div>
		</v-col>

		<v-col cols="6" class="flex-auto mx-auto">
			<div class="inventory-form">
				<div class="form-input">
					<label>Style Code</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="stockData.styleCode"
						disabled
					></v-text-field>
				</div>

				<div class="form-input">
					<label>Description</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="stockData.description"
						disabled
					></v-text-field>
				</div>
			</div>

			<div class="inventory-table">
				<div class="inventory-table-container">
					<div class="flex-table" role="table-head">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="column-header">Size</div>
							<div class="flex-table-cell" role="column-header">Bin #</div>
							<div class="flex-table-cell" role="column-header">QTY On Hand</div>
							<div class="flex-table-cell" role="column-header">Restock Qty</div>
							<div class="flex-table-cell" role="column-header">QTY Committed</div>
						</div>
					</div>
					<!-- inventory items with sizes -->
					<div
						v-if="stockData.sizeChart && stockData.sizeChart.sizes.length > 0"
						class="flex-table"
						role="table-body"
					>
						<div
							v-for="(data, i) in stockData.sizeChart.sizes"
							:key="i"
							class="flex-table-row"
							role="row-group"
						>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="data.name"
										disabled
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="data.inventoryInfo.binNumber"
										:append-outer-icon="
											i === 0 && data.inventoryInfo.binNumber && canUpdateBin
												? 'mdi-transfer-down'
												: ''
										"
										@click:append-outer="
											() => applyAllBin(data.inventoryInfo.binNumber, stockData.sizeChart.sizes)
										"
										:disabled="!canUpdateBin"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input align-center">
									<v-icon
										v-if="
											data.inventoryInfo?.infiniteInventory ||
											stockData.inventoryInfo?.infiniteInventory
										"
										>mdi-infinity</v-icon
									>
									<v-text-field
										v-else
										class="number-input"
										outlined
										dense
										hide-details="auto"
										v-model="data.inventoryInfo.onHand"
										type="number"
										min="0"
										:append-outer-icon="
											i === 0 && data.inventoryInfo.onHand && canUpdateBin
												? 'mdi-transfer-down'
												: ''
										"
										@click:append-outer="
											() => applyAllOnHand(data.inventoryInfo.onHand, stockData.sizeChart.sizes)
										"
										:disabled="!canUpdateBin"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input align-center">
									<v-icon
										v-if="
											data.inventoryInfo?.infiniteInventory ||
											stockData.inventoryInfo?.infiniteInventory
										"
										>mdi-infinity</v-icon
									>
									<v-text-field
										v-else
										class="number-input"
										outlined
										dense
										hide-details="auto"
										v-model="data.inventoryInfo.restockQuantity"
										type="number"
										min="0"
										:append-outer-icon="
											i === 0 && data.inventoryInfo.restockQuantity && canUpdateBin
												? 'mdi-transfer-down'
												: ''
										"
										@click:append-outer="
											() => applyAllRestock(data.inventoryInfo.restockQuantity, stockData.sizeChart.sizes)
										"
										:disabled="!canUpdateBin"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="data.inventoryInfo.committed"
										disabled
									></v-text-field>
								</div>
							</div>
						</div>
					</div>
					<!-- inventory sub items -->
					<div v-else class="flex-table" role="table-body">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										:value="'N/A'"
										disabled
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="stockData.inventoryInfo.binNumber"
										:disabled="!canUpdateBin"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input align-center">
									<v-icon v-if="stockData.inventoryInfo?.infiniteInventory">mdi-infinity</v-icon>
									<v-text-field
										v-else
										class="number-input"
										outlined
										dense
										hide-details="auto"
										v-model="stockData.inventoryInfo.onHand"
										type="number"
										min="0"
										:disabled="!canUpdateBin"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input align-center">
									<v-icon v-if="stockData.inventoryInfo?.infiniteInventory">mdi-infinity</v-icon>
									<v-text-field
										v-else
										class="number-input"
										outlined
										dense
										hide-details="auto"
										v-model="stockData.inventoryInfo.restockQuantity"
										type="number"
										min="0"
										:disabled="!canUpdateBin"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="stockData.inventoryInfo.committed"
										disabled
									></v-text-field>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="inventory-table-total">
					<div class="flex-table" role="table-body">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="cell">Total</div>
							<div class="flex-table-cell" role="cell">&nbsp;</div>
							<div class="flex-table-cell justify-center" role="cell">
								<v-icon v-if="stockData.inventoryInfo?.infiniteInventory">mdi-infinity</v-icon>
								<v-text-field
									v-else
									solo
									flat
									dense
									hide-details="auto"
									:value="totalOnHand"
									disabled
								></v-text-field>
							</div>
							<div class="flex-table-cell" role="cell">&nbsp;</div>
							<div class="flex-table-cell" role="cell">
								<v-text-field
									solo
									flat
									dense
									hide-details="auto"
									:value="totalCommitted"
									disabled
								></v-text-field>
							</div>
						</div>
					</div>
				</div>
			</div>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { hasPermission, SystemPermissions } from "@/models/User";
import { GET_ANY_ITEM_BY_ID } from "@/gql/inventory";
import { getApiUrl } from "@/helpers";

export default {
	name: "BinUtilityInventory",
	props: {
		inventoryItemId: {
			type: Number,
			required: true,
		},
	},
	data() {
		return {
			stockData: {
				inventoryItemId: null,
				styleCode: "",
				description: "",
				sizeChart: {
					sizes: [
						{
							inventoryInfo: {
								binNumber: "",
								quantity: 0,
							},
							sizeId: null,
							name: "",
						},
					],
				},
			},
		};
	},
	methods: {
		async getStockItemById() {
			try {
				const item = await this.getAnyInventoryItem(this.inventoryItemId);

				let res;
				//get stockItem by category type
				if (item?.systemCategoryId && this.$systemCategories.isSubItem(item.systemCategoryId)) {
					res = await this.$apollo.query({
						query: gql`query GetSubItem {
                            subItemById(id: ${this.inventoryItemId}) {
                                inventoryItemId
                                styleCode
                                description
                                inventoryInfo {
                                    binNumber
                                    onHand
                                    committed
									restockQuantity
									infiniteInventory
                                }
                            }
                        }`,
						fetchPolicy: "no-cache",
					});

					console.log("getting inventory sub item", res);
					this.stockData = res.data.subItemById;
					//assign an empty object over null inventoryInfo data
					if (this.stockData.inventoryInfo == null) {
						this.stockData.inventoryInfo = {
							binNumber: "",
							quantity: 0,
							committed: 0,
							restockQuantity: null,
						};
					}
				} else {
					res = await this.$apollo.query({
						query: gql`query GetInventoryItem {
                            inventoryItemById(id: ${this.inventoryItemId}) {
                                inventoryItemId
                                styleCode
                                description
                                sizeChart {
                                    sizes {
                                        sizeId
                                        name
										sequence
										isRetired
										showSize
										inventoryInfo{
											binNumber
											onHand
											committed
											restockQuantity
										}
                                    }
                                }
                                inventoryInfo {
                                    binNumber
                                    onHand
                                    committed
									restockQuantity
									infiniteInventory
                                }
                            }
                        }`,
						fetchPolicy: "no-cache",
					});

					console.log("getting inventory item", res);
					this.stockData = res.data.inventoryItemById;
				}
			} catch (error) {
				console.log(error);
			} finally {
				if (this.stockData.sizeChart && this.stockData.sizeChart.sizes.length > 0) {
					// Filter out retired sizes and sort by sequence to match Size Chart order
					this.stockData.sizeChart.sizes = this.stockData.sizeChart.sizes
						.filter((s) => !s.isRetired && s.showSize)
						.sort((a, b) => (a.sequence ?? 1000) - (b.sequence ?? 1000));

					// Then handle null inventoryInfo if needed
					if (this.stockData.sizeChart.sizes[0].inventoryInfo == null) {
						this.stockData.sizeChart.sizes = this.stockData.sizeChart.sizes.map((obj) => {
							return {
								name: obj.name,
								sizeId: obj.sizeId,
								isRetired: obj.isRetired,
								showSize: obj.showSize,
								inventoryInfo: {
									binNumber: "",
									onHand: 0,
									committed: 0,
									restockQuantity: null,
								},
							};
						});
					}
				} else if (this.stockData.inventoryInfo == null) {
					this.stockData.inventoryInfo = {
						binNumber: "",
						onHand: 0,
						committed: 0,
						restockQuantity: null,
					};
				}
			}
		},

		async saveItemStock() {
			try {
				let inventoryStock;
				if (this.stockData.sizeChart && this.stockData.sizeChart.sizes.length > 0) {
					inventoryStock = {
						inventoryItemId: this.stockData.inventoryItemId,
						stockedSizes: this.stockData.sizeChart.sizes.map((obj) => {
							let binData = {
								sizeId: obj.sizeId,
								binNumber: obj.inventoryInfo.binNumber,
								quantity: parseInt(obj.inventoryInfo.onHand),
								restockQuantity: this.toRestockValue(obj.inventoryInfo.restockQuantity),
							};
							return binData;
						}),
					};
				} else {
					inventoryStock = {
						inventoryItemId: this.stockData.inventoryItemId,
						binNumber: this.stockData.inventoryInfo.binNumber,
						quantity: parseInt(this.stockData.inventoryInfo.onHand),
						restockQuantity: this.toRestockValue(this.stockData.inventoryInfo.restockQuantity),
					};
				}

				const res = await this.$http.put(
					getApiUrl("/api/inventory/bins"),
					inventoryStock
				);
				console.log(res);

				this.$router.push("/inventory/bin-utility");
			} catch (error) {
				console.log(error);
			}
		},

		applyAllBin(bin, sizes) {
			if (sizes && bin) {
				sizes.forEach((s) => (s.inventoryInfo.binNumber = bin));
			}
		},

		applyAllOnHand(onHand, sizes) {
			if (sizes && onHand) {
				sizes.forEach((s) => (s.inventoryInfo.onHand = onHand));
			}
		},

		applyAllRestock(restockQuantity, sizes) {
			if (sizes && restockQuantity) {
				sizes.forEach((s) => (s.inventoryInfo.restockQuantity = restockQuantity));
			}
		},

		// Blank/null restock input means "no target set" -> send null (clears the target).
		toRestockValue(value) {
			if (value === null || value === "" || value === undefined) {
				return null;
			}
			return parseInt(value);
		},

		async getAnyInventoryItem(itemId) {
			let response = {};

			try {
				const res = await this.$apollo.query({
					query: GET_ANY_ITEM_BY_ID,
					variables: {
						id: itemId,
					},
					fetchPolicy: "no-cache",
				});

				console.log("getting inventory item", res);
				return res.data.inventoryItem;
			} catch (error) {
				console.log(error);
			}

			return response;
		},
	},
	computed: {
		canUpdateBin() {
			const user = this.$store.getters.userInfo;
			return hasPermission(user, SystemPermissions.Inventory_BinUtility_Update) || user.isAdmin;
		},
		totalOnHand() {
			let totalOnHand = [];
			if (this.stockData.sizeChart && this.stockData.sizeChart.sizes.length > 0) {
				this.stockData.sizeChart.sizes.forEach((obj) => {
					obj.inventoryInfo.onHand == ""
						? null
						: totalOnHand.push(parseInt(obj.inventoryInfo.onHand));
				});
			} else {
				totalOnHand.push(parseInt(this.stockData.inventoryInfo.onHand));
			}

			return totalOnHand.reduce((a, b) => a + b, 0);
		},
		totalCommitted() {
			let totalCommitted = [];
			if (this.stockData.sizeChart && this.stockData.sizeChart.sizes.length > 0) {
				this.stockData.sizeChart.sizes.forEach((obj) => {
					obj.inventoryInfo.committed == ""
						? null
						: totalCommitted.push(parseInt(obj.inventoryInfo.committed));
				});
			} else {
				totalCommitted.push(parseInt(this.stockData.inventoryInfo.committed));
			}

			return totalCommitted.reduce((a, b) => a + b, 0);
		},
	},
	mounted() {
		//check if stock data is passed in and get that items data
		if (this.inventoryItemId !== null) {
			this.getStockItemById();
		}
	},
};
</script>

<style lang="scss" scoped>
.inventory {
	&-actions {
		display: flex;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			margin-left: 0.5rem;
		}
	}

	&-table {
		display: flex;
		flex-flow: column nowrap;
		height: 100%;
		margin-top: 2rem;

		&-container {
			flex-grow: 1;
			background-color: $color-white;

			.flex-table-cell {
				.form-input {
					width: 100%;
				}

				&:nth-child(1) {
					width: 20%;
				}
				&:nth-child(2) {
					width: 20%;
				}
				&:nth-child(3) {
					width: 20%;
				}
				&:nth-child(4) {
					width: 20%;
				}
				&:nth-child(5) {
					width: 20%;
				}
			}
		}

		&-total {
			background-color: $color-gray-light;

			.flex-table {
				border: 1px solid $color-gray;
			}

			.flex-table-cell {
				.form-input {
					width: 100%;
				}

				&:nth-child(1) {
					width: 20%;
				}
				&:nth-child(2) {
					width: 20%;
				}
				&:nth-child(3) {
					width: 20%;
				}
				&:nth-child(4) {
					width: 20%;
				}
				&:nth-child(5) {
					width: 20%;
				}
			}
		}
	}

	&-preview {
		display: block;
		width: 3rem;
		height: 3rem;
		border-radius: 25px;
	}
}
</style>
